import React, { useState } from "react";

import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	FormControlLabel,
	Checkbox,
	Alert,
	CircularProgress,
	TextField,
	Box,
} from "@mui/material";

import { createApi } from "../../services/api";
import toastError from "../../errors/toastError";
import { BotaoCancelar, BotaoConfirmar } from "../BotoesModal";

// Data de hoje no formato YYYY-MM-DD (horário local, sem cair no fuso do UTC).
const hojeYMD = () => {
	const d = new Date();
	const mes = String(d.getMonth() + 1).padStart(2, "0");
	const dia = String(d.getDate()).padStart(2, "0");
	return `${d.getFullYear()}-${mes}-${dia}`;
};

// Extrai o nome do arquivo do cabeçalho Content-Disposition (usa filename*
// com acentos quando presente, senão o filename simples).
const parseFileName = (contentDisposition, fallback) => {
	if (!contentDisposition) return fallback;
	const star = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
	if (star) {
		try {
			return decodeURIComponent(star[1]);
		} catch (e) {
			/* segue pro filename simples */
		}
	}
	const simple = /filename="?([^";]+)"?/i.exec(contentDisposition);
	return simple ? simple[1] : fallback;
};

const ExportTicketModal = ({ open, onClose, ticket }) => {
	const [withMedia, setWithMedia] = useState(false);
	const [fullConversation, setFullConversation] = useState(true);
	const [startDate, setStartDate] = useState(hojeYMD());
	const [endDate, setEndDate] = useState(hojeYMD());
	const [loading, setLoading] = useState(false);

	const invalidRange = !fullConversation && startDate > endDate;

	const resetState = () => {
		setWithMedia(false);
		setFullConversation(true);
		setStartDate(hojeYMD());
		setEndDate(hojeYMD());
	};

	const handleClose = () => {
		if (loading) return;
		resetState();
		onClose();
	};

	const handleExport = async () => {
		if (!ticket?.id || invalidRange) return;
		setLoading(true);
		try {
			// Multiplexing: mensagens ficam no backend dono da conexão do ticket.
			const apiCustom = createApi(ticket?.whatsapp?.urlBackend);
			const params = { withMedia };
			if (!fullConversation) {
				params.startDate = startDate;
				params.endDate = endDate;
			}
			const response = await apiCustom.get(`/tickets/${ticket.id}/export`, {
				params,
				responseType: "blob",
			});

			const fallbackName = `conversa_${ticket.id}.${withMedia ? "zip" : "xlsx"}`;
			const fileName = parseFileName(
				response.headers["content-disposition"],
				fallbackName
			);

			const url = window.URL.createObjectURL(new Blob([response.data]));
			const link = document.createElement("a");
			link.href = url;
			link.download = fileName;
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);

			resetState();
			onClose();
		} catch (err) {
			toastError(err);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
			<DialogTitle>Exportar conversa</DialogTitle>
			<DialogContent sx={{ display: "flex", flexDirection: "column" }}>
				<FormControlLabel
					control={
						<Checkbox
							checked={fullConversation}
							onChange={(e) => setFullConversation(e.target.checked)}
							disabled={loading}
						/>
					}
					label="Conversa inteira"
				/>

				{!fullConversation && (
					<Box sx={{ display: "flex", gap: 2, mt: 1, mb: 1 }}>
						<TextField
							label="De"
							type="date"
							size="small"
							fullWidth
							value={startDate}
							onChange={(e) => setStartDate(e.target.value)}
							disabled={loading}
							InputLabelProps={{ shrink: true }}
						/>
						<TextField
							label="Até"
							type="date"
							size="small"
							fullWidth
							value={endDate}
							onChange={(e) => setEndDate(e.target.value)}
							disabled={loading}
							InputLabelProps={{ shrink: true }}
							error={invalidRange}
							helperText={invalidRange ? "A data final não pode ser antes da inicial." : ""}
						/>
					</Box>
				)}

				<FormControlLabel
					control={
						<Checkbox
							checked={withMedia}
							onChange={(e) => setWithMedia(e.target.checked)}
							disabled={loading}
						/>
					}
					label="Incluir as mídias"
				/>

				{withMedia && (
					<Alert severity="info" sx={{ mt: 1 }}>
						As mídias ficam no nosso servidor por até 30 dias.
					</Alert>
				)}
			</DialogContent>
			<DialogActions>
				<BotaoCancelar onClick={handleClose} disabled={loading}>
					Cancelar
				</BotaoCancelar>
				<BotaoConfirmar
					onClick={handleExport}
					disabled={loading || invalidRange}
					startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
				>
					{loading ? "Exportando..." : "Exportar"}
				</BotaoConfirmar>
			</DialogActions>
		</Dialog>
	);
};

export default ExportTicketModal;
